import { useMemo } from 'react'

import Fuse, { Expression, IFuseOptions } from 'fuse.js'

import { getEligibleSearchablePages } from '../eligibility'
import { EligibilityDeps } from '../eligibility/types'
import { STANDARD_PAGES, STRICT_MATCH_PAGES } from '../searchablePages'
import { CATEGORY_PRIORITY, EligiblePagesDebugInfo, SearchablePageMetadata, UniversalSearchResult } from '../types'

/** Minimum query length to trigger search */
// Minimum of 3 filters out 1-2 character "stop words" (e.g., "a", "an", "to", "or")
// that don't provide meaningful search context and add noise to results
const MIN_QUERY_LENGTH = 3

/** Default debounce delay in milliseconds for search input */
export const DEFAULT_SEARCH_DEBOUNCE_MS = 500

/**
 * Base Fuse.js configuration for fuzzy matching searchable pages.
 * - useExtendedSearch enables $and/$or logical operators for multi-word queries
 * - displayName has higher weight (3) than keywords (1)
 * - ignoreLocation matches anywhere in the string, not just the beginning
 */
const BASE_FUSE_OPTIONS: IFuseOptions<SearchablePageMetadata> = {
  useExtendedSearch: true,
  keys: [
    { name: 'displayName', weight: 3 },
    { name: 'keywords', weight: 1 },
  ],
  minMatchCharLength: MIN_QUERY_LENGTH,
  includeScore: true,
  ignoreLocation: true,
}

/**
 * Standard Fuse.js configuration with threshold 0.3 for normal fuzzy matching.
 * Threshold 0.3 requires ~70% character similarity, reducing false positives
 * for short or nonsensical queries while still allowing reasonable typo tolerance.
 */
const STANDARD_FUSE_OPTIONS: IFuseOptions<SearchablePageMetadata> = {
  ...BASE_FUSE_OPTIONS,
  threshold: 0.3,
}

/**
 * Strict Fuse.js configuration with threshold 0.1 for near-exact matching.
 * Used for sensitive pages (strictMatchOnly: true) where vague searches should not surface the item.
 * Allows minor typos and word order differences but rejects vague partial matches.
 */
const STRICT_FUSE_OPTIONS: IFuseOptions<SearchablePageMetadata> = {
  ...BASE_FUSE_OPTIONS,
  threshold: 0.1,
}

/**
 * Builds a Fuse.js query expression from user input.
 * - Single word: returns the word as-is for simple fuzzy search
 * - Multi-word: returns $and expression requiring all tokens to match
 *   in either displayName OR keywords
 *
 * @example
 * buildSearchQuery("therapy") // returns "therapy"
 * buildSearchQuery("therapy couples") // returns { $and: [{ $or: [...] }, { $or: [...] }] }
 */
const buildSearchQuery = (query: string): string | Expression => {
  const tokens = query.split(/\s+/).filter((token) => token.length >= MIN_QUERY_LENGTH)

  if (tokens.length === 0) {
    return query
  }

  if (tokens.length === 1) {
    return tokens[0]
  }

  // Multi-word: require all tokens to match in displayName OR keywords
  // Each token must match in at least one of the searchable fields
  const andExpressions: Expression[] = tokens.map((token) => ({
    $or: [{ displayName: token } as Expression, { keywords: token } as Expression],
  }))

  return { $and: andExpressions }
}

export interface UseUniversalSearchOptions {
  /** Search query string */
  query: string
  /** Eligibility dependencies for pre-filtering */
  eligibilityDeps: EligibilityDeps
  /** Maximum number of results to return */
  limit?: number
}

export interface UseUniversalSearchResult {
  /** Search results sorted by relevance */
  results: UniversalSearchResult[]
  /** Debug info about eligible pages */
  eligiblePagesDebugInfo: EligiblePagesDebugInfo
}

/**
 * Compares two search results for sorting.
 * Primary sort: Fuse.js score (lower = better match)
 * Secondary sort: Category priority (lower = higher rank)
 * Tertiary sort: Item priority within category (lower = higher rank)
 */
const compareSearchResults = (a: UniversalSearchResult, b: UniversalSearchResult): number => {
  // Primary: Score (lower is better)
  const scoreDiff = a.score - b.score
  if (Math.abs(scoreDiff) > 0.001) {
    return scoreDiff
  }

  // Secondary: Category priority (lower is higher rank)
  const aCategoryPriority = CATEGORY_PRIORITY[a.item.category]
  const bCategoryPriority = CATEGORY_PRIORITY[b.item.category]
  if (aCategoryPriority !== bCategoryPriority) {
    return aCategoryPriority - bCategoryPriority
  }

  // Tertiary: Item priority within category (lower is higher rank)
  const aItemPriority = a.item.priority ?? 999
  const bItemPriority = b.item.priority ?? 999
  return aItemPriority - bItemPriority
}

/**
 * Hook for universal search with fuzzy matching and eligibility pre-filtering.
 *
 * The search pipeline:
 * 1. Pre-filter: Filter SEARCHABLE_PAGES by user eligibility (O(n) once, memoized)
 * 2. Index: Create Fuse.js index from eligible pages (memoized, only rebuilds when eligibility changes)
 * 3. Match: Fuzzy search using Fuse.js (only when query.length >= 3)
 * 4. Rank: Sort by score, then category priority, then item priority
 *
 * @param options - Search options including query and eligibility deps
 * @returns Array of search results, sorted by relevance
 *
 * @example
 * ```tsx
 * const eligibilityDeps = useEligibilityDeps()
 * const [query, setQuery] = useState('')
 *
 * const results = useUniversalSearch({
 *   query,
 *   eligibilityDeps,
 *   limit: 10,
 * })
 * ```
 */
export const useUniversalSearch = ({
  query,
  eligibilityDeps,
  limit,
}: UseUniversalSearchOptions): UseUniversalSearchResult => {
  // Step 1: Pre-filter pages by eligibility (memoized on eligibilityDeps)
  // Uses pre-split arrays from searchablePages.ts for strict vs standard matching
  const eligibleStrictPages = useMemo(
    () => getEligibleSearchablePages(eligibilityDeps, STRICT_MATCH_PAGES),
    [eligibilityDeps],
  )
  const eligibleStandardPages = useMemo(
    () => getEligibleSearchablePages(eligibilityDeps, STANDARD_PAGES),
    [eligibilityDeps],
  )

  // Build debug info from eligible pages (memoized)
  const eligiblePagesDebugInfo: EligiblePagesDebugInfo = useMemo(() => {
    const allEligible = [...eligibleStrictPages, ...eligibleStandardPages]
    return {
      totalCount: allEligible.length,
      strictCount: eligibleStrictPages.length,
      standardCount: eligibleStandardPages.length,
      pageNames: allEligible.map((page) => page.displayName),
    }
  }, [eligibleStrictPages, eligibleStandardPages])

  // Step 2: Create memoized Fuse instances (only rebuilds when eligible pages change)
  const strictFuse = useMemo(() => new Fuse(eligibleStrictPages, STRICT_FUSE_OPTIONS), [eligibleStrictPages])
  const standardFuse = useMemo(() => new Fuse(eligibleStandardPages, STANDARD_FUSE_OPTIONS), [eligibleStandardPages])

  // Step 4 & 5: Search both indexes and combine results (memoized on query and fuse instances)
  const results = useMemo(() => {
    const trimmedQuery = query.trim()

    // Early return for short queries
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      return []
    }

    // Build search query (handles multi-word tokenization)
    const searchQuery = buildSearchQuery(trimmedQuery)

    // Perform fuzzy search on both indexes
    const strictResults = strictFuse.search(searchQuery)
    const standardResults = standardFuse.search(searchQuery)

    // Map to UniversalSearchResult format and combine
    const searchResults: UniversalSearchResult[] = [
      ...strictResults.map((result) => ({
        item: result.item,
        score: result.score ?? 1,
      })),
      ...standardResults.map((result) => ({
        item: result.item,
        score: result.score ?? 1,
      })),
    ]

    // Sort combined results by score → category priority → item priority (still needs to be defined)
    searchResults.sort(compareSearchResults)

    return limit ? searchResults.slice(0, limit) : searchResults
  }, [query, strictFuse, standardFuse, limit])

  return { results, eligiblePagesDebugInfo }
}
